// Libraries
import { Box, Fade, Grid } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef, GridRowSelectionModel, useGridApiRef } from "@mui/x-data-grid-premium";

// Components
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import NoResultAddAnyway from "./NoResultAddAnyway";

// Types
import { IInventoryDesiredVehicles } from "@/DataPages/api/crm/queries";

// Constants
import { CUSTOM_PROPS, SLOTS } from "@/DataPages/components/Inventory/Tables/commonProps";
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";
import { DISABLED_MULTIPLE_SELECTION, MAX_DESIRED_VEHICLE } from "../../../constants";

// Helpers
import { or } from "@/Common/helpers/sonar";

// Types
type TDesiredVehicleTableProps = {
	selectedRows: string[];
	data: IInventoryDesiredVehicles[];
	isLoading: boolean;
	displayTable: boolean;
	columns: GridColDef[];
	isSearchDone: boolean;
	hasNextPage: boolean;
	disabled: boolean;
	amountSelectedVehicles: number;
	onSelectedRows: (rows: string[]) => void;
	onAddAnyway?: () => void;
};

const SELECTABLE_COLUMN: GridColDef = {
	field: GRID_CHECKBOX_SELECTION_COL_DEF.field,
	width: 40,
	minWidth: 40,
	maxWidth: 40,
	filterable: false,
	sortable: false,
	resizable: false,
	renderHeader: () => <></>,
};

export default function DesiredVehicleTable(props: Readonly<TDesiredVehicleTableProps>) {
	const {
		displayTable,
		data,
		isLoading,
		columns,
		selectedRows,
		isSearchDone,
		hasNextPage,
		disabled,
		amountSelectedVehicles,
		onSelectedRows,
		onAddAnyway,
	} = props;

	const apiRef = useGridApiRef();

	function onSelectInventory(newSelection: string[]) {
		onSelectedRows(newSelection);
	}

	if (!displayTable || !isSearchDone) {
		return null;
	}

	if (isLoading) {
		return (
			<Grid className="mt-[20px]">
				<TableLoader toolbar rows={3} content toolbarClasses="hidden" toolbarSx={{ marginBottom: "10px" }} />
			</Grid>
		);
	}

	return (
		<DataSmartTable
			apiRef={apiRef}
			columns={[SELECTABLE_COLUMN, ...columns]}
			rows={data}
			maxRows={6}
			enableCustomScrollbars
			disableColumnMenu
			disableColumnResize
			hideFooter
			checkboxSelection
			rowSelectionModel={selectedRows}
			isRowSelectable={({ row }) => {
				const { _id } = row as { _id: string };
				return (
					!disabled &&
					((selectedRows.length < MAX_DESIRED_VEHICLE && amountSelectedVehicles < MAX_DESIRED_VEHICLE) ||
						selectedRows.includes(_id))
				);
			}}
			slots={{
				...SLOTS,
				noRowsOverlay: (props) => (
					<NoResultAddAnyway
						{...props}
						linkText="add anyway"
						onLinkClick={onAddAnyway}
						disabled={or(
							!DISABLED_MULTIPLE_SELECTION && amountSelectedVehicles >= MAX_DESIRED_VEHICLE,
							DISABLED_MULTIPLE_SELECTION && amountSelectedVehicles >= 1
						)}
					/>
				),
			}}
			slotProps={{
				toolbar: {
					customComponent: (
						<Fade in={hasNextPage} timeout={400}>
							<Box className="my-[16px]">
								<LoadingSpinner label="Load more inventory..." className="items-center" />
							</Box>
						</Fade>
					),
				},
				noRowsOverlay: {
					title: "No inventory results found",
				},
			}}
			customProps={{
				...CUSTOM_PROPS,
				tableClassName: {
					...CUSTOM_PROPS.tableClassName,
					"-mt-[30px]": true,
				},
			}}
			rowHeight={25}
			autoHeight={false}
			columnVisibilityModel={{ _id: false }}
			onRowSelectionModelChange={(rowIds: GridRowSelectionModel) => {
				onSelectInventory(rowIds as string[]);
			}}
			disableRowSelectionOnClick
			disableColumnSelector
			disableMultipleRowSelection={DISABLED_MULTIPLE_SELECTION} // Remove this property if you want to allow multiple row selection with limit (MAX_DESIRED_VEHICLE)
		/>
	);
}
